import { render } from "preact";
import { App } from "./app";
import { startPeonFocusAudio } from "./audio";
import { watchForUpdates } from "./update";
import { installViewportGuard } from "./viewport";
import { reportLostPhotoPick } from "./photo-pick";
import { nativePlatform } from "./components/InstallHint";
import "./styles.css";
import "./styles-forms.css";

// iOS Safari ignores user-scalable=no in the browser tab (it honours it when
// installed to the home screen), so also cancel its proprietary pinch events.
// The photo viewer opts back in by handling pointer events on its own stage.
for (const type of ["gesturestart", "gesturechange", "gestureend"]) {
  document.addEventListener(type, (e) => {
    if (!(e.target as Element | null)?.closest?.(".lightbox")) e.preventDefault();
  }, { passive: false });
}

if ("serviceWorker" in navigator && !location.hostname.endsWith("localhost") && location.hostname !== "127.0.0.1") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then(watchForUpdates).catch(() => {});
  });
}

startPeonFocusAudio();
installViewportGuard();

/**
 * The Android app (docs/android.md), before the first render: a link that opened the app (a
 * notification tap) is applied, then a photo Android re-delivers after killing the app behind the
 * camera is picked up and the app lands on the screen it belongs to, so the pick is restored rather
 * than reported lost; then the hardware back button. These modules load only inside that app.
 */
async function startAndroidShell(): Promise<void> {
  try {
    await (await import("./native-android")).installAppLinks();
  } catch (e) {
    console.warn("App links unavailable", e);
  }
  try {
    await (await import("./native-camera")).installPhotoRestore();
  } catch (e) {
    console.warn("Photo restore unavailable", e);
  }
  void import("./native-android").then((m) => m.installBackButton()).catch(() => {});
}

function start(): void {
  reportLostPhotoPick();
  render(<App />, document.getElementById("app")!);
}

if (nativePlatform() === "android") void startAndroidShell().then(start);
else start();
